/**
 * utilities/efrisState.js
 * ----------------------------------------------------------------------------
 * The one reading of a vendor's EFRIS identity, shared by every screen that
 * shows it: the wallet dashboard's EFRIS column and filter, and the Vendor tax
 * settings page under Tax.
 *
 * WHY THIS IS A MODULE AND NOT A LINE IN EACH VIEW. Ops tells three states
 * apart because they mean different things operationally: filing LIVE to URA,
 * set up but still in the sandbox or waiting on enrolment, and not set up at
 * all. The wallet dashboard used to derive the state inline for its column and
 * again for its filter, and the two could only stay in agreement by accident.
 * With a second screen reading the same contracts, one helper is the only way
 * a vendor cannot be "Filing live" on one page and "Set up, not live" on
 * another while URA is watching.
 *
 * The input is a row from GET /api/vendor-commissions (see
 * services/vendorCommissionService.js), whose `efris` block is already the
 * resolved identity: `tin` may be inherited from the VAT number, in which case
 * `tinSource` is "VAT" and nobody has confirmed it against URA's records.
 */

/** @typedef {"LIVE" | "SETUP" | "NONE"} EfrisStateKey */

/**
 * @param {object} contract a vendor commission contract row
 * @returns {EfrisStateKey}
 */
export const efrisState = (contract) => {
  const efris = contract?.efris || {};
  if (efris.mode === "LIVE" && efris.enrolmentStatus === "ACTIVE") return "LIVE";
  return efris.tin ? "SETUP" : "NONE";
};

/** Filter options, in the order ops reads them: live first, then set up, then missing. */
export const EFRIS_STATE_OPTIONS = Object.freeze([
  { key: "LIVE", label: "Filing live" },
  { key: "SETUP", label: "Set up, not live" },
  { key: "NONE", label: "Not set up" },
]);

/**
 * What a table cell says about the state: the badge text, its tone, and the
 * quiet line under it (the TIN, flagged when inherited). Written once so the
 * wallet column and the tax page cannot word the same state differently.
 *
 * `tone` is one of green | purple | neutral, the names the wallet badge
 * palette already uses; the tax page maps the same names onto its pills.
 *
 * @param {object} contract
 * @returns {{ state: EfrisStateKey, label: string, tone: string, meta: string|null }}
 */
export const describeEfrisState = (contract) => {
  const efris = contract?.efris || {};
  const state = efrisState(contract);
  if (state === "LIVE") {
    return { state, label: "FILING LIVE", tone: "green", meta: `TIN ${efris.tin}` };
  }
  if (state === "SETUP") {
    return {
      state,
      label: efris.enrolmentStatus === "ACTIVE" ? "TEST" : String(efris.enrolmentStatus || "PENDING"),
      tone: "purple",
      meta: `TIN ${efris.tin}${efris.tinSource === "VAT" ? " (from VAT)" : ""}`,
    };
  }
  return { state, label: "NOT SET UP", tone: "neutral", meta: null };
};
